import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Snackbar,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

import type { TaskResponse } from "api/model";
import { hasServerRecord, useOrthoWorkflows } from "context/OrthoWorkflow";

import OrthoFolderItem from "./OrthoFolderItem";
import SidePanelSectionHeader from "./SidePanelSectionHeader";
import UploadFlightDialogContents from "./UploadFlightDialogContents";

interface ImageryPanelProps {}

const ImageryPanel: React.FC<ImageryPanelProps> = () => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const clearFilesOnExitRef = useRef(false);

  const {
    isPending,
    upload: {
      activeSession,
      clearActiveSession,
      isAnyUploadInProgress,
      setErrorMessage,
      setUploadStatus,
      startNewSession,
      startResumeSession,
    },
    workflows,
  } = useOrthoWorkflows();

  const errorMessage = activeSession?.errorMessage ?? null;
  const uploadStatus = activeSession?.status ?? "idle";
  const resumeTasks = activeSession?.resumeTasks ?? null;

  // Flights still being picked in the dialog (or rejected before any task was
  // created) live in the dialog and its error banner, not in this list.
  const listedWorkflows = workflows.filter(hasServerRecord);

  const onUploadFlightClick = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    startNewSession();
    setUploadDialogOpen(true);
  };

  const onReupload = (orthoId: number, siteId: number) => {
    (document.activeElement as HTMLElement | null)?.blur();
    startNewSession({ orthoId, siteId });
    setUploadDialogOpen(true);
  };

  const onResumeUpload = (tasks: TaskResponse[]) => {
    startResumeSession(tasks);
    setUploadDialogOpen(true);
  };

  const onCloseUploadDialog = useCallback(() => {
    clearFilesOnExitRef.current =
      uploadStatus === "idle" || uploadStatus === "error";
    setUploadDialogOpen(false);
  }, [uploadStatus]);

  useEffect(() => {
    if (uploadStatus === "error" && uploadDialogOpen && resumeTasks !== null) {
      clearFilesOnExitRef.current = true;
      setUploadDialogOpen(false);
    }
  }, [uploadStatus, uploadDialogOpen, resumeTasks]);

  const onExitedUploadDialog = () => {
    if (clearFilesOnExitRef.current) {
      clearFilesOnExitRef.current = false;
      clearActiveSession();
    }
  };

  const onSnackbarClose = () => {
    setErrorMessage(null);
    setUploadStatus("idle");
  };

  const showEmptyState = listedWorkflows.length === 0 && !isPending;

  return (
    <>
      <Snackbar
        open={errorMessage != null}
        onClose={onSnackbarClose}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Alert severity="error" onClose={onSnackbarClose}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Dialog
        disablePortal
        open={uploadDialogOpen}
        onClose={onCloseUploadDialog}
        maxWidth="md"
        fullWidth
        slotProps={{ transition: { onExited: onExitedUploadDialog } }}
      >
        <UploadFlightDialogContents onCancel={onCloseUploadDialog} />
      </Dialog>
      <SidePanelSectionHeader
        action={
          <Button
            color="primary"
            disabled={isAnyUploadInProgress}
            onClick={onUploadFlightClick}
            size="small"
            sx={{ gap: 1 }}
            variant="contained"
          >
            <CloudUploadOutlinedIcon />
            Upload flight
          </Button>
        }
        title="Imagery & Orthos"
      />
      {showEmptyState ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            py: 2,
          }}
        >
          <CloudUploadOutlinedIcon color="secondary" />
          <Typography variant="subtitle1">No imagery yet</Typography>
          <Typography variant="body2">
            Ortho creation starts automatically after each drone flight is
            uploaded.
          </Typography>
        </Box>
      ) : isPending ? (
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            py: 2,
          }}
        >
          <CircularProgress aria-label="Loading upload jobs" size={24} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", mx: -2 }}>
          {listedWorkflows.map((workflow) => (
            <OrthoFolderItem
              key={workflow.key}
              onReupload={onReupload}
              onResumeUpload={onResumeUpload}
              workflowKey={workflow.key}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default ImageryPanel;
